<template>
  <nav class="navbar navbar-expand" id="userNavbar">
    <div class="container-fluid">

      <a class="navbar-link btn link-with-icon" @click="goBack1Page()" @keyup.space="goBack1Page()">
        <stihl-icon icon="chevron-left"/>
        {{ $t('adm.button.back') }}
      </a>

      <div class="collapse navbar-collapse d-flex justify-content-end">
        <ul class="navbar-nav">
          <li class="nav-item" v-if="showCRMContactLink">
            <a id="linkCrmContact"
               class="nav-link btn link-with-icon"
               :href="crmLink(user.crmId as string, 'contact')"
               target="_blank"
               rel="noopener noreferrer"
               title="go to CRM contact">
              <img src="@/assets/img/icons/external-link.svg" alt="go to crm contact">
              <span class="d-none d-md-inline">{{ $t('adm.page.userDetail.linkCrmContact') }}</span>
            </a>
          </li>
          <li class="nav-item" v-if="showCRMAccountLink">
            <a id="linkCrmAccount"
               class="nav-link btn link-with-icon"
               :href="crmLink(user.organization, 'account')"
               target="_blank"
               rel="noopener noreferrer"
               title="go to CRM account">
              <img src="@/assets/img/icons/external-link.svg" alt="go to crm account">
              <span class="d-none d-md-inline">{{ $t('adm.page.userDetail.linkCrmAccount') }}</span>
            </a>
          </li>
          <li class="nav-item" v-if="canEditSyncFlag">
            <a id="lnkEditSyncFlag"
               class="nav-link btn link-with-icon"
               @click="showUserSyncModal = user"
               @keyup.space="showUserSyncModal = user"
               title="sychronization">
              <stihl-icon icon="note-check" alt="sync"/>
              <span class="d-none d-md-inline">{{ $t('adm.label.syncFlagShort') }}</span>
              <sync-badge :flag="user.syncFlag"/>
            </a>
          </li>
          <li class="nav-item" v-if="canEditSyncFlag || showCRMAccountLink || showCRMContactLink">
            <div class="divider"/>
          </li>
          <li class="nav-item" v-if="canDeleteAccount">
            <a id="lnkDelete"
               class="nav-link btn link-with-icon"
               @click="$emit('deleteUser')"
               @keyup.space="$emit('deleteUser')"
               title="delete account">
              <img src="@/assets/img/icons/trash.svg" alt="delete account">
              <span class="d-none d-md-inline">{{ $t('adm.page.userDetail.btnDeleteAccount') }}</span>
            </a>
          </li>
          <li class="nav-item" v-if="canChangePassword">
            <a id="lnkPassReset"
               class="nav-link btn link-with-icon"
               @click="$emit('resetPassword')"
               @keyup.space="$emit('resetPassword')"
               title="change password">
              <img src="@/assets/img/icons/arrow-counterclockwise.svg" alt="change password">
              <span class="d-none d-md-inline">{{ $t('adm.page.userDetail.btnPasswordReset') }}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import StihlIcon from '@/components/ui/StihlIcon.vue';
import SyncBadge from '@/components/ui/SyncBadge.vue';
import { mapState, mapWritableState } from 'pinia';
import { levelOf, UserData } from '@/components/backend';
import useAppStore from '@/components/store';
import { isPrimaryContact } from '@/components/utils';

export default defineComponent({
  name: 'UserDetailNavbar',
  components: { SyncBadge, StihlIcon },
  emits: ['deleteUser', 'resetPassword'],
  props: {
    user: { type: Object as PropType<UserData>, required: true },
    isReadOnly: { type: Boolean, default: false },
  },
  computed: {
    ...mapState(useAppStore, {
      myRole: 'getMyAdminRole',
    }),
    ...mapWritableState(useAppStore, {
      showUserSyncModal: 'showUserSyncModal',
    }),
    canEditSyncFlag(): boolean {
      return !this.isReadOnly && !isPrimaryContact(this.user) && this.user.confirmed
          && levelOf(this.myRole) >= levelOf('orgSuperAdmin');
    },
    canDeleteAccount(): boolean {
      return !this.isReadOnly; // && !isPrimaryContact(this.user);
    },
    canChangePassword(): boolean {
      return !this.isReadOnly && this.user.confirmed && levelOf(this.myRole) >= levelOf('orgSuperAdmin');
    },
    showCRMContactLink(): boolean {
      return !!this.user.crmId && levelOf(this.myRole) >= levelOf('orgSuperAdmin');
    },
    showCRMAccountLink(): boolean {
      return !!this.user.organization && levelOf(this.myRole) >= levelOf('orgSuperAdmin');
    },
  },
});
</script>

<style>
#userNavbar {
  --user-nav-height: 60px;
  height: var(--user-nav-height);
  flex-shrink: 0;
  border-bottom: 1px solid var(--grey-mid);
}

#userNavbar .container-fluid {
  --bs-gutter-x: 2.5rem;
  padding: var(--bs-gutter-y) var(--bs-gutter-x);
}

#userNavbar ul.navbar-nav {
  --bs-navbar-nav-link-padding-x: 0;
  gap: 1.88rem;
  align-items: center;
}

#userNavbar .divider {
  width: 1px;
  height: var(--user-nav-height);
  flex-shrink: 0;
  background: var(--grey-mid);
}
</style>
